/**
 * What the system wants this person to see: a cleaner at a locked door, a promise missed, the card
 * disagreeing with the page. Mine and only mine; the server narrows to the signed-in user and
 * nothing here can widen it.
 *
 * Unread first, as the server orders them. Tapping one marks it read and tells the shell to count
 * again, so the bell in the header is right without polling.
 *
 * UNREAD IS GOLD ON NAVY, AS THE BELL'S OWN COUNT IS, and it is not coral. This list painted every
 * unread row coral, which is the one thing coral is not allowed to mean here: coral is a gap
 * somebody has to close, and a message nobody has opened yet is not yet known to be one. Whether a
 * row is a gap is decided by its KIND, in `components/StatusPills.tsx` and nowhere else, and it
 * goes on being a gap after somebody has read it. A missed promise does not stop being missed
 * because the office opened the notification about it.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { HOLD_BECAME_A_STOP_TITLES } from '@fk/shared';
import { ApiError, listNotifications, markNotificationRead, type NotificationRow } from '../api.ts';
import { Card, Pill, ScreenTitle } from '../components/AppShell.tsx';
import { Button } from '../components/Field.tsx';
import { Busy, EmptyNotice, ErrorNotice } from '../components/Notice.tsx';
import { notificationKindTone } from '../components/StatusPills.tsx';
import { formatInstant } from '../format.ts';
import { NOTIFICATION_KIND_LABELS, STOP_HOLD_OUTCOME_LABELS } from '../labels.ts';

/**
 * A HOLD'S SECOND NOTICE (ops #33): the same kind as the first, written when five business days passed
 * undecided and her number was marked. Nothing waits on the Texts screen for it any more, so it opens
 * no texts and its pill says what happened rather than that a decision waits. Told apart by its
 * fixed title, shared with the server that writes it, since the row has no column for the step: any
 * title it has been written with, since a notice written before 2026-10-20 keeps the old one.
 */
const isHoldThatBecameAStop = (row: NotificationRow): boolean =>
  row.kind === 'opt_out_hold' && HOLD_BECAME_A_STOP_TITLES.includes(row.title);

export function Notifications({ onChanged }: { onChanged: () => void }) {
  const [rows, setRows] = useState<readonly NotificationRow[] | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [problem, setProblem] = useState<string | null>(null);

  const reload = useCallback(() => setAttempt((n) => n + 1), []);

  useEffect(() => {
    let live = true;
    setError(null);
    listNotifications()
      .then((answer) => {
        if (live) setRows(answer.notifications);
      })
      .catch((thrown: unknown) => {
        if (!live) return;
        setError(
          thrown instanceof ApiError
            ? thrown
            : new ApiError(0, 'error', 'The notifications did not load.'),
        );
      });
    return () => {
      live = false;
    };
  }, [attempt]);

  async function markRead(id: string) {
    setProblem(null);
    try {
      await markNotificationRead(id);
      onChanged();
      reload();
    } catch (thrown) {
      setProblem(thrown instanceof ApiError ? thrown.message : 'That was not marked.');
    }
  }

  return (
    <div>
      <ScreenTitle title="Notifications" subtitle="What needs a person to look." />

      {problem ? (
        <div className="mb-3">
          <ErrorNotice title="Not marked" message={problem} />
        </div>
      ) : null}

      {error ? (
        <ErrorNotice
          title="The notifications did not load"
          message={error.message}
          onRetry={reload}
        />
      ) : rows === null ? (
        <Busy what="your notifications" />
      ) : rows.length === 0 ? (
        <EmptyNotice title="Nothing to see">
          <p>
            When a cleaner cannot get in, a promise is missed or a kill rule fires, it appears here.
          </p>
        </EmptyNotice>
      ) : (
        <ul className="space-y-3">
          {rows.map((row) => {
            const unread = row.state === 'unread';
            const becameAStop = isHoldThatBecameAStop(row);
            return (
              <li key={row.id}>
                <Card className={unread ? 'border-gold/60' : 'opacity-80'}>
                  <div className="flex flex-wrap items-center gap-2">
                    <Pill tone={notificationKindTone(row.kind)}>
                      {becameAStop
                        ? STOP_HOLD_OUTCOME_LABELS.auto_suppressed
                        : NOTIFICATION_KIND_LABELS[row.kind]}
                    </Pill>
                    {unread ? <Pill tone="gold">Unread</Pill> : null}
                    <span className="text-xs text-navy/60">{formatInstant(row.createdAt)}</span>
                  </div>
                  <h2 className="mt-2 font-serif text-lg leading-snug">{row.title}</h2>
                  {row.body ? <p className="mt-1 text-sm whitespace-pre-line">{row.body}</p> : null}
                  <div className="mt-3 flex flex-wrap gap-2">
                    {row.visitId ? (
                      <Link
                        to={`/visits/${row.visitId}`}
                        className="inline-flex min-h-11 desk:min-h-9 items-center rounded-lg border border-navy/25 bg-cream px-4 text-sm font-medium"
                      >
                        Open the visit
                      </Link>
                    ) : null}
                    {row.invoiceId ? (
                      <Link
                        to={`/invoices/${row.invoiceId}`}
                        className="inline-flex min-h-11 desk:min-h-9 items-center rounded-lg border border-navy/25 bg-cream px-4 text-sm font-medium"
                      >
                        Open the invoice
                      </Link>
                    ) : null}
                    {/* A rule that fired names no row (ops #48): its figures are on the Sources screen. */}
                    {row.kind === 'kill_rule_fired' ? (
                      <Link
                        to="/sources"
                        className="inline-flex min-h-11 desk:min-h-9 items-center rounded-lg border border-navy/25 bg-cream px-4 text-sm font-medium"
                      >
                        Open the sources
                      </Link>
                    ) : null}
                    {/* A stop in her words held for a decision (ops #30) names no row either: the
                        band at the top of the Texts screen is where it is decided (ops #33). Not
                        for the hold that already became a stop: nothing waits in the band. */}
                    {row.kind === 'opt_out_hold' && !becameAStop ? (
                      <Link
                        to="/messages"
                        className="inline-flex min-h-11 desk:min-h-9 items-center rounded-lg border border-navy/25 bg-cream px-4 text-sm font-medium"
                      >
                        Open the texts
                      </Link>
                    ) : null}
                    {unread ? <Button onClick={() => markRead(row.id)}>Mark as read</Button> : null}
                  </div>
                </Card>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
